import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import BillingPlans from './BillingPlans';
import PaymentForm from './PaymentForm';
import { createTeamWithSubscription } from '../api';

const steps = ['Team Details', 'Choose Plan', 'Payment'];

export default function CreateTeamWithBilling({ onSuccess, onCancel }) {
  const [activeStep, setActiveStep] = useState(0);
  const [teamData, setTeamData] = useState({
    name: '',
    description: '',
    image_url: ''
  });
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTeamDataChange = (e) => {
    const { name, value } = e.target;
    setTeamData(prev => ({ ...prev, [name]: value }));
  };

  const calculateFinalPrice = (planData) => {
    if (!planData) return 0;
    
    const basePrice = parseFloat(planData.plan[planData.billingCycle === 'yearly' ? 'yearly_price' : 'monthly_price']);
    if (planData.discount) {
      if (planData.discount.discount_type === 'percentage') {
        return basePrice * (1 - planData.discount.discount_value / 100);
      } else {
        return Math.max(0, basePrice - planData.discount.discount_value);
      }
    }
    return basePrice;
  };

  const handlePlanSelect = (selectedPlanData) => {
    setPlanData(selectedPlanData);

    // Check if payment is required
    const finalPrice = calculateFinalPrice(selectedPlanData)
    if(finalPrice <= 0){
      handleCreateTeamDirectly(selectedPlanData)
    } else{
      setActiveStep(2);
    }
  };

  const handleCreateTeamDirectly = async (planData) => {
    setLoading(true);
    setError('');

    try {
      const finalTeamData = {
        ...teamData,
        planId: planData.planId,
        billingCycle: planData.billingCycle,
        couponCode: planData.couponCode
      };

      const result = await createTeamWithSubscription(finalTeamData);
      onSuccess(result);
    } catch (err) {
      setError(err.message || 'Failed to create team');
      setLoading(false);
    }
  };

  const handlePaymentComplete = async (paymentResult) => {
    if (!paymentResult.success) {
      setError('Payment failed. Please try again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const finalTeamData = {
        ...teamData,
        planId: planData.planId,
        billingCycle: planData.billingCycle,
        couponCode: planData.couponCode
      };

      const result = await createTeamWithSubscription(finalTeamData);
      onSuccess(result);
    } catch (err) {
      setError(err.message || 'Failed to create team');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
      setError('');
    } else {
      onCancel();
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!teamData.name.trim()) {
        setError('Team name is required');
        return;
      }
      setActiveStep(1);
      setError('');
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Create Your Team
            </Typography>
            
            <TextField
              fullWidth
              label="Team Name"
              name="name"
              value={teamData.name}
              onChange={handleTeamDataChange}
              required
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Description (Optional)"
              name="description"
              value={teamData.description}
              onChange={handleTeamDataChange}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Team Image URL (Optional)"
              name="image_url"
              value={teamData.image_url}
              onChange={handleTeamDataChange}
              placeholder="https://example.com/team-image.jpg"
            />
          </Box>
        );
      
      case 1:
        return (
          <BillingPlans 
            onPlanSelect={handlePlanSelect}
            onBack={handleBack}
          />
        );
      
      case 2:
        return (
          <PaymentForm
            planData={planData}
            onPaymentComplete={handlePaymentComplete}
            onBack={handleBack}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h4" sx={{ mb: 3, textAlign: 'center' }}>
            Create New Team
          </Typography>
          
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <CircularProgress />
            </Box>
          )}
          
          {renderStepContent(activeStep)}
          
          {activeStep === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button onClick={onCancel} variant="outlined">
                Cancel
              </Button>
              <Button
                onClick={handleNext}
                variant="contained"
                disabled={loading}
              >
                Next
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
} 